import {
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Right, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';
import {object, string} from 'yup';

import {always, isNil} from 'ramda';

import {MyWebDescriptionFormat, useUpdateMyWebDescriptionFormatMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {useBranchId} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {useMyWebPlatformResolved} from '../hooks/useMyWebPlatformResolved';

type PlatformMyWebDescriptionFormatFormData = {
  descriptionFormat: MyWebDescriptionFormat;
};

const DESCRIPTION_FORMAT_OPTIONS = [
  {label: i18n.t('page.salesSettings.labels.descriptionFormatPlainText'), value: 'plain_text'},
  {label: i18n.t('page.salesSettings.labels.descriptionFormatMarkdown'), value: 'markdown'},
  {label: i18n.t('page.salesSettings.labels.descriptionFormatHtml'), value: 'html'},
];

export function PlatformMyWebDescriptionFormat() {
  const branchId = useBranchId();
  const {activeData, resolvedParams, isFetching, isError, isPublishingOnTenant} =
    useMyWebPlatformResolved();

  const [updateDescriptionFormat] = useUpdateMyWebDescriptionFormatMutation();

  const mode = match([isNil(branchId), isPublishingOnTenant])
    .with([false, true], always('Tenant'))
    .with([false, false], always('Branch'))
    .otherwise(always(''));

  const onSubmit: FormSubmitHandler<PlatformMyWebDescriptionFormatFormData> = (
    values,
    _setErrors,
    reset
  ) =>
    updateDescriptionFormat({
      body: {descriptionFormat: values.descriptionFormat},
      params: resolvedParams,
    })
      .unwrap()
      .then(() => {
        showNotification.success(
          i18n.t('entity.advertisingPlatforms.notifications.defaultSettingsSaved')
        );
        reset(undefined, {keepValues: true});
      })
      .catch(handleApiError);

  const values: PlatformMyWebDescriptionFormatFormData = {
    descriptionFormat: activeData?.descriptionFormat ?? 'plain_text',
  };

  const descriptionFormatLabel = match<[boolean, boolean], string>([
    isNil(branchId),
    isPublishingOnTenant,
  ])
    .with([true, Pattern.any], () => i18n.t('page.salesSettings.labels.descriptionFormat'))
    .with([false, true], () => i18n.t('page.salesSettings.labels.descriptionFormatTenant'))
    .with([false, false], () => i18n.t('page.salesSettings.labels.descriptionFormatBranch'))
    .exhaustive();

  return (
    <Card title={i18n.t('general.labels.settings')}>
      <DataStatus isLoading={isFetching} isError={isError} minHeight={25}>
        <Form<PlatformMyWebDescriptionFormatFormData>
          values={values}
          schema={schema}
          onSubmit={onSubmit}
          key={mode}
          shouldWatchForUnsavedChanges
        >
          {(control) => (
            <VStack spacing={4}>
              <FormField
                control={control}
                name="descriptionFormat"
                type="choice"
                label={descriptionFormatLabel}
                tooltip={i18n.t('page.salesSettings.labels.descriptionFormatTooltip')}
                options={DESCRIPTION_FORMAT_OPTIONS}
              />
              <Right>
                <FormButton
                  control={control}
                  type="submit"
                  title={i18n.t('general.actions.save')}
                />
              </Right>
            </VStack>
          )}
        </Form>
      </DataStatus>
    </Card>
  );
}

const schema = object({
  descriptionFormat: string().oneOf(['plain_text', 'markdown', 'html']).default('plain_text'),
});
